import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchSessionLogData } from 'services/api';

const sortByTimestampAsc = arr => [...arr].sort((a, b) => a.timestamp - b.timestamp);


const useSessionLogData = ({ pgid, enabled }) => {
  const [data, setData] = useState(null);
  const [dataPgid, setDataPgid] = useState(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [errorPgid, setErrorPgid] = useState(null);

  const fetchedOnceRef = useRef(false);
  const requestIdRef = useRef(0);

  // Prevent setting state after unmount.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    requestIdRef.current += 1;
    fetchedOnceRef.current = false;
    setData(null);
    setDataPgid(null);
    setPending(false);
    setError(null);
    setErrorPgid(null);
  }, [pgid]);

  const doFetch = useCallback(async () => {
    if (!pgid) return;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setPending(true);
    setError(null);
    setErrorPgid(null);

    try {
      const raw = await fetchSessionLogData(pgid);
      const arr = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : null;

      if (!arr) {
        throw new Error('Unexpected session logdata response shape (expected array)');
      }

      const sorted = sortByTimestampAsc(arr);

      if (mountedRef.current && requestIdRef.current === requestId) {
        setData(sorted);
        setDataPgid(pgid);
        fetchedOnceRef.current = true;
      }
    } catch (e) {
      if (mountedRef.current && requestIdRef.current === requestId) {
        setError(e);
        setErrorPgid(pgid);
        // allow retry
        fetchedOnceRef.current = false;
      }
    } finally {
      if (mountedRef.current && requestIdRef.current === requestId) {
        setPending(false);
      }
    }
  }, [pgid]);

  // Auto-fetch once when enabled.
  useEffect(() => {
    if (!enabled || !pgid) return;
    if (fetchedOnceRef.current) return;

    fetchedOnceRef.current = true;
    doFetch();
  }, [enabled, pgid, doFetch]);

  const retry = useCallback(() => {
    fetchedOnceRef.current = false;
    doFetch();
  }, [doFetch]);

  const scopedData = dataPgid === pgid ? data : null;
  const scopedError = errorPgid === pgid ? error : null;
  const scopedPending =
    pending || Boolean(enabled && pgid && dataPgid !== pgid && errorPgid !== pgid);

  return [scopedData, scopedPending, scopedError, retry];
};

export default useSessionLogData;
